import { useState, useEffect } from "react";
import { useNavigate, Link } from "react-router-dom";
import { useAuth } from "../App";
import axios from "axios";
import { API } from "../App";
import { 
  Home, Users, ArrowLeft, Search, Filter, Shield, Check, X,
  Phone, Mail, MapPin, Clock, DollarSign, Star, Award, Building2,
  ChevronDown, ChevronUp, Eye
} from "lucide-react";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Badge } from "../components/ui/badge";
import { Checkbox } from "../components/ui/checkbox";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "../components/ui/dialog";

const SKILL_OPTIONS = [
  "Cleaning",
  "Painting",
  "Landscaping / Lawn Care",
  "HVAC",
  "Plumbing",
  "Electrical",
  "Roofing",
  "Handyman / General Repairs",
  "Flooring",
  "Drywall",
  "Tile",
  "Pressure Washing",
  "Appliance Repair",
  "Pest Control"
];

export default function AdminContractors() {
  const { token } = useAuth();
  const navigate = useNavigate();
  
  const [contractors, setContractors] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedContractor, setSelectedContractor] = useState(null);
  const [showDetailDialog, setShowDetailDialog] = useState(false);
  
  // Filters
  const [insuredOnly, setInsuredOnly] = useState(false);
  const [skillFilter, setSkillFilter] = useState("");
  const [serviceAreaSearch, setServiceAreaSearch] = useState("");
  const [subscriptionFilter, setSubscriptionFilter] = useState("");
  const [minScore, setMinScore] = useState("");
  const [maxScore, setMaxScore] = useState("");

  useEffect(() => {
    fetchContractors();
  }, [insuredOnly, skillFilter, subscriptionFilter, minScore, maxScore]);

  const fetchContractors = async () => {
    try {
      let url = `${API}/admin/contractors?`;
      const params = [];
      
      if (insuredOnly) params.push("insured_only=true");
      if (skillFilter) params.push(`skill=${encodeURIComponent(skillFilter)}`);
      if (subscriptionFilter) params.push(`subscription=${encodeURIComponent(subscriptionFilter)}`);
      if (serviceAreaSearch) params.push(`service_area=${encodeURIComponent(serviceAreaSearch)}`);
      if (minScore) params.push(`min_score=${minScore}`);
      if (maxScore) params.push(`max_score=${maxScore}`);
      
      url += params.join("&");
      
      const response = await axios.get(url, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setContractors(response.data);
    } catch (error) {
      /* silent - error deliberately ignored */
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = () => {
    fetchContractors();
  };

  const viewContractorDetail = async (userId) => {
    try {
      const response = await axios.get(`${API}/admin/contractors/${userId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setSelectedContractor(response.data);
      setShowDetailDialog(true);
    } catch (error) {
      /* silent - error deliberately ignored */
    }
  };

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <header className="bg-white border-b border-gray-200 sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 py-4 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Link to="/admin" className="flex items-center gap-2 text-[#1F2933]/60 hover:text-[#1F2933]">
              <ArrowLeft className="w-5 h-5" />
              Back to Dashboard
            </Link>
          </div>
          <div className="flex items-center gap-2">
            <div className="w-10 h-10 rounded-xl bg-[#0FA3B1] flex items-center justify-center">
              <Home className="w-5 h-5 text-white" />
            </div>
            <span className="font-manrope font-bold text-xl text-[#1F2933]">Contractor Hub LLC</span>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 py-8">
        <div className="mb-8">
          <h1 className="font-manrope font-bold text-2xl sm:text-3xl text-[#1F2933] mb-2">
            Contractor Management
          </h1>
          <p className="text-[#1F2933]/60">
            View and manage all registered contractors
          </p>
        </div>

        {/* Filters */}
        <div className="bg-white rounded-2xl border border-gray-200 p-6 mb-6">
          <h3 className="font-medium text-[#1F2933] mb-4 flex items-center gap-2">
            <Filter className="w-5 h-5" /> Filters
          </h3>
          
          <div className="grid sm:grid-cols-2 lg:grid-cols-5 gap-4">
            {/* Insured Only */}
            <div className="flex items-center gap-2">
              <Checkbox
                id="insured-only"
                checked={insuredOnly}
                onCheckedChange={setInsuredOnly}
              />
              <label htmlFor="insured-only" className="text-sm text-[#1F2933]">
                Insured Only
              </label>
            </div>

            {/* Skill Filter */}
            <Select value={skillFilter} onValueChange={setSkillFilter}>
              <SelectTrigger>
                <SelectValue placeholder="Filter by skill" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="">All Skills</SelectItem>
                {SKILL_OPTIONS.map(skill => (
                  <SelectItem key={skill} value={skill}>{skill}</SelectItem>
                ))}
              </SelectContent>
            </Select>

            {/* Subscription Filter */}
            <Select value={subscriptionFilter} onValueChange={setSubscriptionFilter}>
              <SelectTrigger>
                <SelectValue placeholder="Subscription tier" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="">All Plans</SelectItem>
                <SelectItem value="starter">Starter</SelectItem>
                <SelectItem value="pro">Pro</SelectItem>
                <SelectItem value="elite">Elite</SelectItem>
                <SelectItem value="none">No Subscription</SelectItem>
              </SelectContent>
            </Select>

            {/* Service Area Search */}
            <div className="relative">
              <Input
                placeholder="Search service area..."
                value={serviceAreaSearch}
                onChange={(e) => setServiceAreaSearch(e.target.value)}
              />
            </div>

            {/* Score Range */}
            <div className="flex items-center gap-2">
              <Input
                type="number"
                placeholder="Min Score"
                value={minScore}
                onChange={(e) => setMinScore(e.target.value)}
                className="w-24"
              />
              <span className="text-[#1F2933]/50">-</span>
              <Input
                type="number"
                placeholder="Max"
                value={maxScore}
                onChange={(e) => setMaxScore(e.target.value)}
                className="w-24"
              />
            </div>

            {/* Search Button */}
            <Button onClick={handleSearch} className="bg-[#0FA3B1] hover:bg-[#0D8C98] text-white">
              <Search className="w-4 h-4 mr-2" /> Search
            </Button>
          </div>
        </div>

        {/* Stats */}
        <div className="grid sm:grid-cols-4 gap-4 mb-6">
          <div className="bg-white rounded-xl p-4 border border-gray-200">
            <p className="text-2xl font-bold text-[#0FA3B1]">{contractors.length}</p>
            <p className="text-sm text-[#1F2933]/60">Total Contractors</p>
          </div>
          <div className="bg-white rounded-xl p-4 border border-gray-200">
            <p className="text-2xl font-bold text-green-600">
              {contractors.filter(c => c.onboarding_complete).length}
            </p>
            <p className="text-sm text-[#1F2933]/60">Onboarded</p>
          </div>
          <div className="bg-white rounded-xl p-4 border border-gray-200">
            <p className="text-2xl font-bold text-blue-600">
              {contractors.filter(c => c.is_insured).length}
            </p>
            <p className="text-sm text-[#1F2933]/60">Insured</p>
          </div>
          <div className="bg-white rounded-xl p-4 border border-gray-200">
            <p className="text-2xl font-bold text-purple-600">
              {contractors.filter(c => c.is_licensed === "Yes").length}
            </p>
            <p className="text-sm text-[#1F2933]/60">Licensed</p>
          </div>
        </div>

        {/* Contractors Table */}
        <div className="bg-white rounded-2xl border border-gray-200 overflow-hidden">
          {loading ? (
            <div className="flex justify-center py-12">
              <div className="w-8 h-8 border-4 border-[#0FA3B1] border-t-transparent rounded-full animate-spin"></div>
            </div>
          ) : contractors.length === 0 ? (
            <div className="text-center py-12">
              <Users className="w-12 h-12 text-[#1F2933]/30 mx-auto mb-4" />
              <p className="text-[#1F2933]/60">No contractors found</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-gray-50 border-b border-gray-200">
                  <tr>
                    <th className="text-left px-4 py-3 text-sm font-medium text-[#1F2933]/70">Name</th>
                    <th className="text-left px-4 py-3 text-sm font-medium text-[#1F2933]/70">Contact</th>
                    <th className="text-left px-4 py-3 text-sm font-medium text-[#1F2933]/70">Skills</th>
                    <th className="text-center px-4 py-3 text-sm font-medium text-[#1F2933]/70">Insured</th>
                    <th className="text-center px-4 py-3 text-sm font-medium text-[#1F2933]/70">Subscription</th>
                    <th className="text-center px-4 py-3 text-sm font-medium text-[#1F2933]/70">Credits</th>
                    <th className="text-center px-4 py-3 text-sm font-medium text-[#1F2933]/70">Score</th>
                    <th className="text-center px-4 py-3 text-sm font-medium text-[#1F2933]/70">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {contractors.map((contractor) => (
                    <tr key={contractor.user_id} className="hover:bg-gray-50">
                      <td className="px-4 py-4">
                        <p className="font-medium text-[#1F2933]">
                          {contractor.full_name || contractor.business_name || "N/A"}
                        </p>
                      </td>
                      <td className="px-4 py-4">
                        <p className="text-sm text-[#1F2933]/70">{contractor.email}</p>
                        <p className="text-sm text-[#1F2933]/50">{contractor.phone}</p>
                      </td>
                      <td className="px-4 py-4">
                        <div className="flex flex-wrap gap-1 max-w-[200px]">
                          {contractor.skills?.slice(0, 3).map(skill => (
                            <Badge key={skill} variant="outline" className="text-xs">
                              {skill}
                            </Badge>
                          ))}
                          {contractor.skills?.length > 3 && (
                            <Badge variant="outline" className="text-xs">
                              +{contractor.skills.length - 3}
                            </Badge>
                          )}
                        </div>
                      </td>
                      <td className="px-4 py-4 text-center">
                        {contractor.is_insured ? (
                          <Badge className="bg-green-100 text-green-800">
                            <Check className="w-3 h-3 mr-1" /> Yes
                          </Badge>
                        ) : (
                          <Badge variant="outline" className="text-[#1F2933]/50">
                            No
                          </Badge>
                        )}
                      </td>
                      <td className="px-4 py-4 text-center">
                        {contractor.subscription_status === "active" ? (
                          <Badge className="bg-purple-100 text-purple-800">
                            {contractor.subscription_plan?.charAt(0).toUpperCase() + contractor.subscription_plan?.slice(1) || "Active"}
                          </Badge>
                        ) : (
                          <Badge variant="outline" className="text-[#1F2933]/50">
                            None
                          </Badge>
                        )}
                      </td>
                      <td className="px-4 py-4 text-center">
                        <span className="font-medium text-amber-600">
                          {contractor.credit_balance || 0}
                        </span>
                      </td>
                      <td className="px-4 py-4 text-center">
                        <span className={`font-bold ${
                          contractor.contractor_score >= 70 ? 'text-green-600' :
                          contractor.contractor_score >= 40 ? 'text-amber-600' :
                          'text-[#1F2933]/50'
                        }`}>
                          {contractor.contractor_score || 0}
                        </span>
                      </td>
                      <td className="px-4 py-4 text-center">
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => viewContractorDetail(contractor.user_id)}
                        >
                          <Eye className="w-4 h-4" />
                        </Button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </main>

      {/* Contractor Detail Dialog */}
      <Dialog open={showDetailDialog} onOpenChange={setShowDetailDialog}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Contractor Details</DialogTitle>
          </DialogHeader>
          
          {selectedContractor && (
            <div className="space-y-6 pt-4">
              {/* Basic Info */}
              <div className="flex items-start justify-between">
                <div>
                  <h3 className="font-bold text-xl text-[#1F2933]">
                    {selectedContractor.profile?.full_name || selectedContractor.profile?.business_name}
                  </h3>
                  <p className="text-[#1F2933]/60">{selectedContractor.profile?.email}</p>
                  <p className="text-[#1F2933]/60">{selectedContractor.profile?.phone}</p>
                </div>
                <div className="text-right">
                  <p className="text-sm text-[#1F2933]/60">Contractor Score</p>
                  <p className="text-3xl font-bold text-[#0FA3B1]">
                    {selectedContractor.profile?.contractor_score || 0}
                  </p>
                </div>
              </div>

              {/* Status badges */}
              <div className="flex flex-wrap gap-2">
                {selectedContractor.profile?.onboarding_complete && (
                  <Badge className="bg-green-100 text-green-800">Onboarded</Badge>
                )}
                {selectedContractor.profile?.is_insured && (
                  <Badge className="bg-blue-100 text-blue-800">Insured</Badge>
                )}
                {selectedContractor.profile?.is_licensed === "Yes" && (
                  <Badge className="bg-purple-100 text-purple-800">Licensed</Badge>
                )}
              </div>

              {/* Details Grid */}
              <div className="grid sm:grid-cols-2 gap-4">
                <div>
                  <p className="text-sm text-[#1F2933]/60">Skills</p>
                  <div className="flex flex-wrap gap-1 mt-1">
                    {selectedContractor.profile?.skills?.map(skill => (
                      <Badge key={skill} variant="outline" className="text-xs">{skill}</Badge>
                    ))}
                  </div>
                </div>
                <div>
                  <p className="text-sm text-[#1F2933]/60">Experience</p>
                  <p className="text-[#1F2933]">{selectedContractor.profile?.years_experience || "N/A"}</p>
                </div>
                <div>
                  <p className="text-sm text-[#1F2933]/60">Service Area</p>
                  <p className="text-[#1F2933]">{selectedContractor.profile?.service_area || "N/A"}</p>
                </div>
                <div>
                  <p className="text-sm text-[#1F2933]/60">Business Type</p>
                  <p className="text-[#1F2933]">{selectedContractor.profile?.business_type || "N/A"}</p>
                </div>
                <div>
                  <p className="text-sm text-[#1F2933]/60">Job Sizes</p>
                  <div className="flex flex-wrap gap-1 mt-1">
                    {selectedContractor.profile?.job_size_accepted?.map(size => (
                      <Badge key={size} variant="outline" className="text-xs">{size}</Badge>
                    ))}
                  </div>
                </div>
                <div>
                  <p className="text-sm text-[#1F2933]/60">Availability</p>
                  <p className="text-[#1F2933]">{selectedContractor.profile?.availability || "N/A"}</p>
                </div>
                <div>
                  <p className="text-sm text-[#1F2933]/60">Free Estimates</p>
                  <p className="text-[#1F2933]">{selectedContractor.profile?.free_estimates ? "Yes" : "No"}</p>
                </div>
                <div>
                  <p className="text-sm text-[#1F2933]/60">Contact Preference</p>
                  <p className="text-[#1F2933]">{selectedContractor.profile?.preferred_contact_method || "N/A"}</p>
                </div>
              </div>

              {/* Reviews */}
              {selectedContractor.profile?.reviews_platforms?.length > 0 && (
                <div>
                  <p className="text-sm text-[#1F2933]/60 mb-2">Review Platforms</p>
                  <div className="flex flex-wrap gap-2">
                    {selectedContractor.profile.reviews_platforms.map(platform => (
                      <Badge key={platform} className="bg-amber-100 text-amber-800">{platform}</Badge>
                    ))}
                  </div>
                  {selectedContractor.profile.reviews_links && (
                    <p className="text-sm text-[#0FA3B1] mt-2 break-all">
                      {selectedContractor.profile.reviews_links}
                    </p>
                  )}
                </div>
              )}

              {/* Files */}
              <div className="flex gap-4">
                {selectedContractor.profile?.insurance_proof_url && (
                  <Badge className="bg-blue-100 text-blue-800">
                    <Shield className="w-3 h-3 mr-1" /> Insurance Proof Uploaded
                  </Badge>
                )}
                {selectedContractor.profile?.work_photos_url && (
                  <Badge className="bg-green-100 text-green-800">
                    <Check className="w-3 h-3 mr-1" /> Work Photos Uploaded
                  </Badge>
                )}
              </div>

              {/* Created Date */}
              <div className="text-sm text-[#1F2933]/50">
                Created: {selectedContractor.profile?.created_at ? 
                  new Date(selectedContractor.profile.created_at).toLocaleDateString() : 
                  "N/A"
                }
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
